import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import Navigation from '../components/Navigation';
import { useTranslation } from 'react-i18next';

const UserSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: user?.email || '',
    profession: '',
    country: '',
    city: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await api.put('/auth/update-profile', formData);
      toast.success(t('saveChanges'));
      window.location.reload();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Failed to update profile'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft size={16} className="mr-2" />
          {t('back')}
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {t('userSettings')}
            </CardTitle>
            <CardDescription>
              {t('updateProfile')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first-name">
                    {t('firstName')} *
                  </Label>
                  <Input
                    id="first-name"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        first_name: e.target.value,
                      })
                    }
                    required
                    data-testid="firstname-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last-name">
                    {t('lastName')} *
                  </Label>
                  <Input
                    id="last-name"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        last_name: e.target.value,
                      })
                    }
                    required
                    data-testid="lastname-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">
                  {t('email')} *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      email: e.target.value,
                    })
                  }
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="profession">
                  {t('profession')} ({t('optional')})
                </Label>
                <Input
                  id="profession"
                  value={formData.profession}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      profession: e.target.value,
                    })
                  }
                  data-testid="profession-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="country">
                    {t('country')} ({t('optional')})
                  </Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        country: e.target.value,
                      })
                    }
                    data-testid="country-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city">
                    {t('city')} ({t('optional')})
                  </Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        city: e.target.value,
                      })
                    }
                    data-testid="city-input"
                  />
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                  data-testid="cancel-btn"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isLoading}
                  data-testid="save-btn"
                >
                  {isLoading ? t('loading') : t('saveChanges')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserSettings;
